<template>
  <div class="disclaimer-overlay" @click.self="handleOverlayClick">
    <div class="disclaimer-container">
      <!-- Step 1: Disclaimer Notice -->
      <div v-if="currentStep === 1" class="disclaimer-content">
        <h2 class="disclaimer-title">Disclaimer Notice</h2>
        
        <div class="disclaimer-text">
          <p>Providing a social media handle is optional and is intended for the convenience of the customer. If you choose to input one, you confirm that you are the lawful owner of the handle or have obtained explicit consent from the lawful owner to provide access.</p>
          
          <p>You grant My Oxytocin the right to access this page solely to select photos necessary to complete your order.</p>
          
          <p>If you do not own the handle and have not received authorization from the lawful owner, My Oxytocin will not be able to process your order, and providing unauthorized information may result in order cancellation.</p>
        </div>
        
        <div class="disclaimer-agreement">
          <label class="checkbox-container">
            <input type="checkbox" v-model="agreedToTerms">
            <span class="checkmark"></span>
            <span class="checkbox-text">I agree to and acknowledge these terms</span>
          </label>
        </div>
        
        <button class="nav-arrow right" @click="proceedToStep2" :disabled="!agreedToTerms">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <!-- Step 2: First Question -->
      <div v-if="currentStep === 2" class="questionnaire-content">
        <h2 class="questionnaire-title">Access Questionnaire</h2>
        
        <p class="question-text">Are you the lawful owner of the social media account?</p>
        
        <div class="answer-buttons">
          <button class="answer-btn" @click="handleOwnershipAnswer(true)">Yes</button>
          <button class="answer-btn" @click="handleOwnershipAnswer(false)">No</button>
        </div>
      </div>

      <!-- Step 3: Second Question (if not owner) -->
      <div v-if="currentStep === 3" class="questionnaire-content">
        <h2 class="questionnaire-title">Access Questionnaire</h2>
        
        <p class="question-text">If you are not owner, have you obtained explicit consent from the lawful owner to provide access?</p>
        
        <div class="answer-buttons">
          <button class="answer-btn" @click="handleConsentAnswer(true)">Yes</button>
          <button class="answer-btn" @click="handleConsentAnswer(false)">No</button>
        </div>
      </div>

      <!-- Step 4: Privacy Notice (Success) -->
      <div v-if="currentStep === 4" class="privacy-notice-content">
        <h2 class="privacy-title">Privacy Notice</h2>
        
        <div class="privacy-text">
          <p>The photos will be used for product design only. If your pet account is private, we will request follow @myoxytocin_pets. Your order will be canceled and you will receive a full refund if you do not accept the request in 24 hours.</p>
          <p>Please select next to proceed.</p>
        </div>
        
        <button class="nav-arrow right" @click="completeSuccess">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <!-- Step 5: Access Notice (Rejection) -->
      <div v-if="currentStep === 5" class="access-notice-content">
        <h2 class="access-title">Access Notice</h2>
        
        <div class="access-text">
          <p>We are unable to proceed with customization with a social media account due to unmet access requirements. As a result, we've switched your customization method to image upload only. If this was your intended choice, feel free to go back and review your answers. If not, please proceed with the customization via uploaded images option.</p>
        </div>
        
        <div class="navigation-buttons">
          <button class="nav-arrow left" @click="goBackToQuestions">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          
          <button class="nav-arrow right" @click="completeRejection">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Progress dots -->
      <div class="progress-dots" v-if="currentStep <= 3">
        <span class="dot" :class="{ active: currentStep >= 1, filled: currentStep > 1 }"></span>
        <span class="dot" :class="{ active: currentStep >= 2, filled: currentStep > 2 }"></span>
        <span class="dot" :class="{ active: currentStep >= 3, filled: currentStep > 3 }" v-if="showStep3"></span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SocialMediaDisclaimer',
  data() {
    return {
      currentStep: 1,
      agreedToTerms: false,
      ownershipAnswer: null,
      consentAnswer: null,
      showStep3: false
    };
  },
  methods: {
    handleOverlayClick() {
      // Prevent closing by clicking outside
    },
    proceedToStep2() {
      if (this.agreedToTerms) {
        this.currentStep = 2;
      }
    },
    handleOwnershipAnswer(isOwner) {
      this.ownershipAnswer = isOwner;
      if (isOwner) {
        // If they are the owner, show privacy notice
        this.currentStep = 4;
      } else {
        // If not owner, automatically go to step 3
        this.showStep3 = true;
        this.currentStep = 3;
      }
    },

    handleConsentAnswer(hasConsent) {
      this.consentAnswer = hasConsent;
      if (hasConsent === false) {
        // No consent, automatically show access notice
        this.currentStep = 5;
      } else {
        // Has consent, show privacy notice
        this.currentStep = 4;
      }
    },

    goBackToQuestions() {
      // Reset and go back to step 2
      this.currentStep = 2;
      this.ownershipAnswer = null;
      this.consentAnswer = null;
      this.showStep3 = false;
    },
    completeSuccess() {
      const data = {
        agreedToTerms: this.agreedToTerms,
        isOwner: this.ownershipAnswer,
        hasConsent: this.consentAnswer
      };
      this.$emit('accepted', data);
    },
    completeRejection() {
      const data = {
        agreedToTerms: this.agreedToTerms,
        isOwner: this.ownershipAnswer,
        hasConsent: this.consentAnswer
      };
      this.$emit('rejected', data);
    }
  }
};
</script>

<style scoped>
.disclaimer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.disclaimer-container {
  background: white;
  border-radius: 1vw;
  padding: 2vw;
  max-width: 40vw;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
}

.disclaimer-content,
.questionnaire-content,
.privacy-notice-content,
.access-notice-content {
  text-align: center;
}

.disclaimer-title,
.questionnaire-title,
.privacy-title,
.access-title {
  font-size: 2vw;
  font-weight: 600;
  margin-bottom: 1.5vw;
  color: #333;
}

.disclaimer-text,
.privacy-text,
.access-text {
  text-align: left;
  margin-bottom: 2vw;
}

.disclaimer-text p,
.privacy-text p,
.access-text p {
  font-size: 1vw;
  line-height: 1.6;
  color: #333;
  margin-bottom: 1vw;
}

.privacy-text,
.access-text {
  text-align: center;
}

.disclaimer-agreement {
  margin: 2vw 0;
}

.checkbox-container {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1vw;
  user-select: none;
}

.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkmark {
  width: 1.2vw;
  height: 1.2vw;
  border: 0.1vw solid #999;
  border-radius: 0.2vw;
  margin-right: 0.8vw;
  position: relative;
  transition: all 0.3s;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: #2196F3;
  border-color: #2196F3;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
  left: 0.4vw;
  top: 0.1vw;
  width: 0.3vw;
  height: 0.6vw;
  border: solid white;
  border-width: 0 0.15vw 0.15vw 0;
  transform: rotate(45deg);
}

.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

.checkbox-text {
  color: #333;
}

.question-text {
  font-size: 1.2vw;
  color: #333;
  margin-bottom: 2vw;
}

.answer-buttons {
  display: flex;
  gap: 2vw;
  justify-content: center;
  margin-bottom: 2vw;
}

.answer-btn {
  padding: 0.8vw 3vw;
  font-size: 1.1vw;
  border: 0.1vw solid #ddd;
  background: white;
  border-radius: 0.5vw;
  cursor: pointer;
  transition: all 0.3s;
}

.answer-btn:hover {
  background: #f5f5f5;
  border-color: #999;
}

.answer-btn:active {
  background: #e0e0e0;
}

.navigation-buttons {
  display: flex;
  justify-content: center;
  gap: 2vw;
  margin-top: 2vw;
}

.nav-arrow {
  width: 3vw;
  height: 3vw;
  border-radius: 50%;
  border: 0.15vw solid #333;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 auto;
}

.nav-arrow:hover:not(:disabled) {
  background: #f5f5f5;
  transform: scale(1.1);
}

.nav-arrow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-arrow svg {
  width: 1.5vw;
  height: 1.5vw;
}

.bottom-nav {
  margin-top: 2vw;
}

.progress-dots {
  display: flex;
  justify-content: center;
  gap: 0.5vw;
  margin-top: 2vw;
}

.dot {
  width: 0.8vw;
  height: 0.8vw;
  border-radius: 50%;
  background: #ddd;
  transition: all 0.3s;
}

.dot.active {
  background: #666;
}

.dot.filled {
  background: #333;
}

/* Mobile styles */
@media (max-width: 768px) {
  .disclaimer-container {
    max-width: 90vw;
    padding: 5vw;
    border-radius: 3vw;
  }

  .disclaimer-title,
  .questionnaire-title,
  .privacy-title,
  .access-title {
    font-size: 5vw;
    margin-bottom: 4vw;
  }

  .disclaimer-text p,
  .privacy-text p,
  .access-text p {
    font-size: 3.5vw;
    margin-bottom: 3vw;
  }

  .checkbox-container {
    font-size: 3.5vw;
  }

  .checkmark {
    width: 4vw;
    height: 4vw;
    margin-right: 2vw;
  }

  .checkmark:after {
    left: 1.3vw;
    top: 0.3vw;
    width: 1vw;
    height: 2vw;
    border-width: 0 0.5vw 0.5vw 0;
  }

  .question-text {
    font-size: 4vw;
    margin-bottom: 6vw;
  }

  .answer-buttons {
    gap: 4vw;
    margin-bottom: 6vw;
  }

  .answer-btn {
    padding: 3vw 8vw;
    font-size: 4vw;
    border-radius: 2vw;
  }

  .navigation-buttons {
    gap: 4vw;
  }

  .nav-arrow {
    width: 8vw;
    height: 8vw;
    border: 0.3vw solid #333;
  }

  .nav-arrow svg {
    width: 4vw;
    height: 4vw;
  }

  .progress-dots {
    gap: 2vw;
    margin-top: 5vw;
  }

  .dot {
    width: 2.5vw;
    height: 2.5vw;
  }
}
</style> 